// ---------------------------------------------------------------------------
// seo-product.ja.ts — Japanese translations for 6 SEO product landing pages
// ---------------------------------------------------------------------------
import type { SEOProductText } from './seo-product.content';

export const SEO_PRODUCT_JA: Record<string, SEOProductText> = {
  'hotel-management-software': {
    heroTitle: 'ホテル管理ソフトウェア — 施設全体を一括管理',
    heroSubtitle: 'フロントデスク、予約、ハウスキーピング、請求、ゲストコミュニケーションをひとつのクラウドプラットフォームから自動化。ダイナミックプライシング、AIコンシェルジュ、予測オペレーションを含む25のAI機能搭載。40カ国以上、2,500以上のホテルで導入。',
    heroCtaText: '無料トライアル開始',
    problemTitle: '最新ソフトウェアなしのホテルが直面する課題',
    problemPoints: ['手作業のチェックイン・チェックアウトがスタッフの時間を毎日浪費', 'OTAチャネルの未連携によるオーバーブッキングが評判を損なう', '未追跡の料金や請求ミスによる収益漏れ', '稼働率トレンドやRevPARパフォーマンスの可視性なし', 'サービスの遅さやコミュニケーション不足によるゲストの不満', '地域税務報告やゲスト登録のコンプライアンス問題'],
    features: [
      { title: 'フロントデスク＆予約', description: 'リアルタイム客室ボード、ドラッグ＆ドロップチェックイン、ウォークイン予約、グループ予約をひとつのダッシュボードで。' },
      { title: 'チャネルマネージャー', description: 'Booking.com、Expedia、Airbnb、Agodaを含む100以上のOTAとの双方向同期。GDS接続とレートパリティ監視。' },
      { title: 'レベニューインテリジェンス', description: 'リアルタイムRevPAR、ADR、稼働率分析。ダイナミックプライシングルールとWhat-ifシミュレーター。' },
      { title: 'ゲストCRM', description: '嗜好、コミュニケーション履歴、ロイヤルティポイント、支出分析を含む360度ゲストプロファイル。' },
      { title: '請求＆決済', description: '自動請求書発行、分割請求、マルチ通貨対応、地域税務コンプライアンス、Stripe/PayPal連携。' },
      { title: 'コンプライアンス＆セキュリティ', description: '地域税務自動化、ゲスト登録、GDPR準拠、監査証跡、ロールベースアクセス制御、AES-256暗号化。' },
      { title: '25のAI機能', description: 'AIダイナミックプライシング、ゲストコンシェルジュチャットボット、スマートハウスキーピングスケジューリング、ナイトオーディット異常検知、センチメント分析、需要予測、その他19のAI機能をすべてのモジュールに統合。' },
    ],
    benefitsTitle: 'HotelStack.ioで実証された成果',
    benefits: [
      { label: '手作業の削減', description: '自動化がルーチン業務を処理し、スタッフはゲスト対応に集中' },
      { label: '直接予約の増加', description: '統合予約エンジンがOTA手数料を排除' },
      { label: '稼働率の向上', description: 'レベニューインテリジェンスが最適な料金設定で稼働率を最大化' },
      { label: '平均応答時間', description: '24時間対応の専任サポートで2時間以内の応答' },
      { label: 'オンボーディング時間', description: '48時間以内の無料データ移行とセットアップ' },
      { label: '稼働率SLA', description: 'マルチゾーン冗長構成のエンタープライズグレード信頼性' },
    ],
    testimonial: { quote: 'HotelStack.ioに切り替えてから、稼働率が23%上がり、手作業のエラーはほぼゼロになりました。スタッフは直感的な操作を気に入っています。', role: '総支配人', hotel: 'The Greenwich Inn、ロンドン' },
    faqItems: [
      { question: 'ホテル管理ソフトウェアには何が含まれますか？', answer: 'HotelStack.ioにはPMS、チャネルマネージャー、予約エンジン、ゲストCRM、レベニューインテリジェンス、請求、ハウスキーピング、コンプライアンスツール、マルチプロパティ管理がすべて含まれます。' },
      { question: '小規模ホテルに適していますか？', answer: 'はい。月額49ドルのStarterプランは50室以下の施設向けに設計されており、すべてのコア機能を制限なく利用できます。地域別価格もあります。' },
      { question: 'セットアップにどのくらい時間がかかりますか？', answer: 'ほとんどのホテルが48時間以内に稼働開始します。当社チームがデータ移行、スタッフトレーニング、チャネル設定を追加費用なしで行います。' },
      { question: '購入前に試すことはできますか？', answer: 'もちろんです。すべてのプランに14日間の無料トライアルが含まれ、全機能にアクセスできます。クレジットカード不要。' },
    ],
    ctaTitle: 'ホテル業務の近代化を始めませんか？',
    ctaSubtitle: '40カ国以上、2,500以上のホテルがHotelStack.ioで業務を自動化し、収益を向上させています。',
    relatedPages: [
      { label: 'ホテルPMSソフトウェア', description: 'プロパティ管理システムの詳細機能' },
      { label: 'チャネルマネージャー', description: 'OTA同期でオーバーブッキングを解消する方法' },
      { label: 'レベニュー管理', description: 'ダイナミックプライシングとRevPAR最適化' },
      { label: '予約エンジン', description: '直接予約の促進とOTA手数料の排除' },
      { label: 'ゲストCRM', description: '360度プロファイルとロイヤルティ管理' },
    ],
  },
  'hotel-pms-software': {
    heroTitle: 'ホテルPMSソフトウェア — クラウドプロパティ管理システム',
    heroSubtitle: 'ホテル向けAI搭載クラウドプロパティ管理システム。ダイナミックプライシング、スマートハウスキーピング、ナイトオーディット異常検知、AIドリブンインサイト。客室、予約、チェックインをあらゆるデバイスから管理。',
    heroCtaText: 'PMSを無料で試す',
    problemTitle: 'レガシーPMSがホテルを妨げる理由',
    problemPoints: ['オンプレミスサーバーは高額なIT保守が必要', 'モバイルアクセスなし — スタッフがフロントデスク端末に縛られる', '遅いチェックインプロセスがゲストを不満にさせ行列を作る', '連携されていないシステムはデータの二重入力を意味する', 'リアルタイムレポートなし — 古いデータに基づく意思決定', '最新の決済ゲートウェイとの統合不足'],
    features: [
      { title: 'ビジュアル客室ボード', description: 'リアルタイムステータス付きドラッグ＆ドロップ客室割り当て。稼働、清掃、メンテナンスのカラーコード表示。' },
      { title: '予約管理', description: '空室グリッド付き予約カレンダー。直接、OTA、ウォークイン、グループ予約をひとつのインターフェースで管理。' },
      { title: 'ハウスキーピングダッシュボード', description: 'タスク割り当て、検査チェックリスト、リネン追跡、リアルタイム客室ステータス更新。' },
      { title: 'ナイトオーディットウィザード', description: '事前チェック、収益照合、監査サマリーレポート付き自動日締め処理。' },
      { title: 'ゲストフォリオ管理', description: '料金計上、分割請求、デポジット追跡、法人請求ワークフロー付き統合ゲストフォリオ。' },
      { title: 'リアルタイム同期', description: 'すべての変更が全デバイスに即座に同期。更新不要 — 複数スタッフが同時に作業可能。' },
      { title: 'AI搭載オペレーション', description: 'ダイナミックプライシングエンジン、スマートハウスキーピング優先スケジューリング、ナイトオーディット異常検知、全PMSモジュールにわたるAIドリブンインサイト。' },
    ],
    benefitsTitle: '当社PMSが提供する価値',
    benefits: [
      { label: '平均チェックイン時間', description: '事前入力プロファイルとデジタル署名によるエクスプレスチェックイン' },
      { label: 'ペーパーワーク削減', description: 'デジタルフォリオ、e請求書、自動レポートが手作業の記録を置換' },
      { label: 'クラウドアクセス', description: 'デスクトップ、タブレット、モバイルの任意のブラウザからPMSにアクセス' },
      { label: 'リアルタイム可視性', description: '稼働率、収益、運営KPIのライブダッシュボード' },
    ],
    testimonial: { quote: 'PMSダッシュボードだけで運営が一変しました。チェックインは8分から3分未満に、ナイトオーディットは10分で完了するようになりました。', role: 'レベニューディレクター', hotel: 'Marina Bay Suites、ドバイ' },
    faqItems: [
      { question: 'ホテルPMSとは？', answer: 'プロパティ管理システム（PMS）はホテルの中核業務ソフトウェアです。客室管理、予約、チェックイン・アウト、請求、ハウスキーピング、レポートを処理します。HotelStack.ioのPMSはチャネル管理、レベニューツール、ゲストCRMも統合しています。' },
      { question: 'HotelStack.ioのPMSはクラウドベースですか？', answer: 'はい。当社PMSはソフトウェアのインストール不要で完全にクラウドで動作します。任意のブラウザからアクセスでき、99.9%の稼働率保証付きです。' },
      { question: '現在のPMSから移行できますか？', answer: 'はい。当社チームがあらゆるPMSからの完全なデータ移行を行います。移行は無料で、通常48時間以内に完了します。' },
      { question: 'マルチプロパティチェーンで機能しますか？', answer: 'もちろんです。チェーン管理モジュールで、クロスプロパティレポート、料金管理、スタッフ監督を含む一元管理が可能です。' },
    ],
    ctaTitle: '最新クラウドPMSにアップグレード',
    ctaSubtitle: '最速のクラウドホテルPMS。14日間無料トライアル、セットアップ費用ゼロ、無料移行。',
    relatedPages: [
      { label: 'ホテル管理ソフトウェア', description: 'オールインワンホテル運営プラットフォーム' },
      { label: 'チャネルマネージャー', description: '100以上のOTAへの在庫自動同期' },
      { label: '予約エンジン', description: '手数料なしの直接予約受付' },
    ],
  },
  'hotel-booking-engine': {
    heroTitle: 'ホテル予約エンジン — 直接予約を促進',
    heroSubtitle: 'BookMyRoom.ioのAI搭載予約でOTA手数料を排除。スマートアップセル推薦、AIコンシェルジュ、リアルタイム空室確認、即時確認、シームレスなPMS連携。',
    heroCtaText: '予約エンジンを入手',
    problemTitle: 'OTA依存のコスト',
    problemPoints: ['OTA手数料が予約収益の15-25%を消費', 'ゲストとの直接関係なし — OTAが顧客を所有', 'レートパリティ制限が料金柔軟性を制限', 'OTA決済プロセスによる支払いサイクルの遅延'],
    features: [
      { title: 'BookMyRoom.io掲載', description: 'すべての施設がBookMyRoom.ioで写真、設備、レビュー、料金付きのSEO最適化リスティングを獲得。' },
      { title: '即時確認', description: 'リアルタイム空室確認と即時予約確認。予約は自動的にPMSに同期。' },
      { title: 'ゲストレビュー', description: '信頼を構築しコンバージョン率を向上させる検証済みレビュー。レビュー管理ツール付き。' },
      { title: 'セキュア決済', description: 'StripeとPayPal経由の統合決済処理。マルチ通貨対応、自動領収書、返金管理。' },
      { title: 'コンバージョン分析', description: '予約ファネル、コンバージョン率、トップページ、ソース別収益帰属を追跡。' },
      { title: 'ゲストコミュニケーション', description: '自動予約確認、到着前メール、チェックインリマインダーをメールとSMSで配信。' },
      { title: 'AIコンシェルジュ＆アップセル', description: 'AIチャットボットが予約中にゲストをサポート。客室推薦、パッケージ提案、ゲストプロファイルと嗜好に基づくパーソナライズされたアップセルオファー。' },
    ],
    benefitsTitle: '予約エンジンの成果',
    benefits: [
      { label: '直接予約の増加', description: '手数料なしの直接予約が40%増加' },
      { label: '直接予約の手数料', description: 'BookMyRoom.io予約の収益を100%確保' },
      { label: 'データ取得', description: '完全なプロファイルアクセスでゲストとの関係を自社で管理' },
    ],
    testimonial: { quote: 'BookMyRoom.ioにより最初の四半期で直接予約が40%増加しました。手数料の節約だけでHotelStack.ioのサブスクリプション全額をカバーしました。', role: 'オーナー', hotel: 'Casa del Sol、バルセロナ' },
    faqItems: [
      { question: '予約エンジンはどのように機能しますか？', answer: '施設がBookMyRoom.ioに掲載され、ゲストが検索、客室閲覧、レビュー参照、直接予約を行えます。予約はHotelStack.ioダッシュボードに即座に同期されます。' },
      { question: '直接予約に手数料はかかりますか？', answer: 'いいえ。BookMyRoom.io経由の予約は100%手数料無料です。HotelStack.ioのサブスクリプション料金のみお支払いいただきます。' },
      { question: 'リスティングをカスタマイズできますか？', answer: 'はい。写真、客室説明、料金、ポリシー、設備をHotelStack.ioダッシュボードから直接管理できます。' },
    ],
    ctaTitle: '今日から直接予約を受け付けましょう',
    ctaSubtitle: 'すべてのHotelStack.ioプランにBookMyRoom.io掲載が含まれます。追加費用なし、手数料なし。',
    relatedPages: [
      { label: 'チャネルマネージャー', description: '直接予約とOTA配信を並行管理' },
      { label: 'ホテル管理ソフトウェア', description: '予約エンジン内蔵の完全プラットフォーム' },
      { label: 'ゲストCRM', description: '直接予約データからゲストリレーション構築' },
    ],
  },
  'hotel-channel-manager': {
    heroTitle: 'ホテルチャネルマネージャー — 100以上のOTAをリアルタイム同期',
    heroSubtitle: 'Booking.com、Expedia、Airbnb、Agodaと100以上のチャネルとのAI搭載料金同期。AI競合料金モニタリング、需要ベースプライシング、AmadeusとSabreによるGDS接続。オーバーブッキングの自動防止。',
    heroCtaText: 'チャネルマネージャーを試す',
    problemTitle: '手動チャネル管理の危険性',
    problemPoints: ['手動更新の遅延によるオーバーブッキング', 'OTAペナルティを引き起こすレートパリティ違反', '毎日複数のエクストラネットに費やされる時間', 'どのチャネルが最高のパフォーマンスかの一元的ビューなし'],
    features: [
      { title: 'リアルタイム同期', description: '全チャネルの料金、空室、制限の即時双方向同期。サブセカンド更新。' },
      { title: 'レートパリティモニター', description: '全チャネルの自動レートパリティチェック。格差検出時にアラート。' },
      { title: 'チャネルパフォーマンス', description: 'チャネル間の収益、予約量、ADRを比較。トップパフォーマーを特定し配信を最適化。' },
      { title: '同期ログ', description: 'すべての同期操作の完全な監査証跡。失敗、リトライ、データ不整合をタイムスタンプ付きで追跡。' },
      { title: 'イールド管理', description: 'チャネル別料金ルール、最低滞在日数、販売停止日の設定。チャネルごとの配信戦略を最適化。' },
      { title: '100以上のチャネル', description: 'Booking.com、Expedia、Airbnb、Agoda、TripAdvisor、Hostelworldおよび世界各地のOTAとのネイティブ連携。' },
      { title: 'AI搭載ディストリビューション', description: 'AI競合料金モニタリングがリアルタイムで市場ポジショニングを分析、需要ベースプライシングがチャネルごとの最適料金を提案、インテリジェントなチャネル割り当てがディストリビューションネットワーク全体の収益を最大化。' },
    ],
    benefitsTitle: 'チャネルマネージャーの効果',
    benefits: [
      { label: 'オーバーブッキング', description: 'リアルタイム同期が全チャネルでのダブルブッキングを排除' },
      { label: '接続OTA数', description: 'ひとつのダッシュボードからすべての主要旅行プラットフォームに配信' },
      { label: '週間節約時間', description: '手動エクストラネット更新と料金アップロードを排除' },
    ],
    testimonial: { quote: '以前はOTAエクストラネットの料金更新に毎日2時間費やしていました。HotelStack.ioのチャネルマネージャーで全て自動化。6ヶ月間オーバーブッキングゼロです。', role: 'レベニューマネージャー', hotel: 'Le Marais Hotel、パリ' },
    faqItems: [
      { question: 'HotelStack.ioはどのOTAに接続しますか？', answer: 'Booking.com、Expedia、Airbnb、Agoda、TripAdvisor、Hostelworldおよび世界の地域プラットフォームを含む100以上のチャネルをサポート。AmadeusとSabreによるGDS接続も対応。' },
      { question: '料金の同期速度は？', answer: '全接続チャネルで5秒以内に料金と空室が同期します。リアルタイムエンジンがポーリング間隔なしで継続稼働。' },
      { question: '同期に失敗した場合は？', answer: '失敗した同期は自動リトライされます。即座にアラートを受信し、すべての失敗は詳細とともにログに記録されます。' },
    ],
    ctaTitle: 'チャネル配信を自動化しましょう',
    ctaSubtitle: '100以上のOTAで料金と空室を自動同期。オーバーブッキングも手動更新も不要に。',
    relatedPages: [
      { label: 'レベニュー管理', description: 'ダイナミックルールでチャネル間の料金を最適化' },
      { label: '予約エンジン', description: '手数料なしの直接予約を促進' },
      { label: 'ホテル管理ソフトウェア', description: 'チャネルマネージャー内蔵の完全プラットフォーム' },
    ],
  },
  'hotel-crm': {
    heroTitle: 'ホテルCRM — 360度ゲストリレーションシップ管理',
    heroSubtitle: 'AI搭載ゲストプロファイリング、センチメント分析、パーソナライズ推薦、ロイヤルティプログラム、自動コミュニケーションで永続的なゲスト関係を構築。一度きりのゲストをリピーターに。',
    heroCtaText: 'CRMを無料で試す',
    problemTitle: 'CRMなしでゲストデータが失われる理由',
    problemPoints: ['滞在間で嗜好が忘れられる — パーソナライゼーションなし', '支出履歴、コミュニケーション、フィードバックの統合ビューなし', 'スプレッドシートで管理されるロイヤルティプログラムと手動追跡', 'ゲストインテリジェンス不足によるアップセル機会の損失'],
    features: [
      { title: '360度プロファイル', description: '滞在履歴、嗜好、支出分析、コミュニケーションログ、ロイヤルティステータスを含む統合ゲストプロファイル。' },
      { title: 'ロイヤルティプログラム', description: 'ポイントルールエンジン、特典カタログ、自動ティアアップグレード付きの設定可能なティアベースロイヤルティ。' },
      { title: '自動コミュニケーション', description: '到着前ウェルカムメール、誕生日祝い、フィードバックリクエスト、再エンゲージメントキャンペーン。' },
      { title: '支出分析', description: 'ゲスト生涯価値、滞在あたりの平均支出、好みの客室タイプ、F&B消費パターンを追跡。' },
      { title: '嗜好追跡', description: '客室の好み、食事制限、枕のタイプ、特別な記念日の詳細を記録・記憶。' },
      { title: '直接予約コンバージョン', description: 'CRMデータを活用してパーソナライズされた料金とパッケージを提供し、リピート直接予約を促進。' },
      { title: 'AIゲストインテリジェンス', description: 'AI搭載ゲストプロファイリングが360度行動モデルを構築、センチメント分析がレビューとメッセージでの満足度を監視、予測型離脱検知がリスクのあるゲストを事前に特定。' },
    ],
    benefitsTitle: 'ホテル向けCRMの成果',
    benefits: [
      { label: 'リピートゲスト率', description: 'CRM利用ホテルはリピート予約が35%増加' },
      { label: 'ゲスト生涯価値', description: 'パーソナライズされた体験が平均ゲスト生涯価値を2倍に' },
      { label: '手動フォローアップ削減', description: '自動コミュニケーションがルーチンの接点を処理' },
    ],
    testimonial: { quote: '到着前にゲストの嗜好を把握することで体験が一変しました。CRMモジュール導入以来、リピート予約が35%増加しました。', role: '総支配人', hotel: 'Tokyo Azabu Hotel、東京' },
    faqItems: [
      { question: 'CRMはすべてのプランに含まれますか？', answer: 'はい。ゲストCRMはすべてのHotelStack.ioプランに含まれます。ロイヤルティプログラム管理と高度な分析はProfessionalとEnterpriseプランで利用可能です。' },
      { question: '既存のゲストデータをインポートできますか？', answer: 'はい。あらゆるソースからのゲストデータ移行を当社チームが行います。移行は無料で、通常24時間以内に完了します。' },
      { question: 'メールとSMSに対応していますか？', answer: 'はい。自動メールとSMSが組み込まれています。独自メールドメインの接続またはマネージド配信サービスの利用が可能です。' },
    ],
    ctaTitle: '今日からゲストロイヤルティの構築を',
    ctaSubtitle: 'すべての滞在をリレーションシップに変えましょう。CRMはすべてのHotelStack.ioプランに無料で含まれます。',
    relatedPages: [
      { label: 'ホテル管理ソフトウェア', description: 'CRM内蔵の完全プラットフォーム' },
      { label: '予約エンジン', description: '直接予約からゲストデータを取得' },
      { label: 'レベニュー管理', description: 'ゲストデータを活用した料金最適化' },
    ],
  },
  'hotel-revenue-management': {
    heroTitle: 'ホテルレベニュー管理ソフトウェア — RevPARを最大化',
    heroSubtitle: 'AI搭載ダイナミックプライシング、ML需要予測、AI競合料金モニタリング、収益シミュレーター。AIドリブンの料金決定でRevPARを18-25%最大化。',
    heroCtaText: 'レベニューツールを試す',
    problemTitle: 'インテリジェントな料金設定なしの収益リスク',
    problemPoints: ['固定料金は高需要期に機会損失を生む', '戦略的ポジショニングのための競合価格の可視性なし', '需要データなしの直感的な料金決定', '季節トレンドの予測と料金戦略の計画ができない'],
    features: [
      { title: 'ダイナミックプライシングルール', description: '稼働率閾値、曜日、シーズン、需要シグナルに基づいて料金を自動調整するルール。' },
      { title: '需要予測', description: '過去データ、地域イベント、市場トレンドを使用したML予測で稼働率を予測。' },
      { title: '競合料金モニター', description: 'OTAでの競合価格をリアルタイム追跡。競合の料金変更時に自動アラート。' },
      { title: 'RevPAR分析', description: 'リアルタイムRevPAR、ADR、稼働率ダッシュボード。トレンド分析、ベンチマーク、目標追跡付き。' },
      { title: 'What-ifシミュレーター', description: '実行前に収益シナリオをモデル化。料金変更、最低滞在、パッケージオファーを仮想テスト。' },
      { title: '収益レポート', description: '日次フラッシュレポート、ペース＆ピックアップ分析、チャネル貢献分析、セグメント別パフォーマンス。' },
      { title: '自律型AIプライシング', description: '需要シグナル、競合動向、市場環境、予約パターンに基づいてAIがリアルタイムで料金を調整する24時間365日のハンズフリー料金管理 — 手動介入不要。' },
    ],
    benefitsTitle: 'レベニュー管理の効果',
    benefits: [
      { label: 'RevPAR向上', description: 'ダイナミックプライシングが需要ピーク時の最適収益を確保' },
      { label: '稼働率成長', description: 'スマートな料金戦略がオフピーク時の客室を埋める' },
      { label: 'サブスクリプションROI', description: '収益向上が通常プラットフォームコストの3倍' },
    ],
    testimonial: { quote: 'レベニュー分析ダッシュボードだけでサブスクリプション全額の元が取れました。ダイナミックプライシングで最初の四半期にRevPARが18%向上しました。', role: 'レベニューディレクター', hotel: 'Pacific Resort Group、シドニー' },
    faqItems: [
      { question: 'RevPARとは？', answer: 'Revenue Per Available Room（販売可能客室あたり収益）はホテルの主要業績指標で、ADR × 稼働率で算出されます。HotelStack.ioはRevPARをリアルタイムで追跡し、最大化するツールを提供します。' },
      { question: 'ダイナミックプライシングに対応していますか？', answer: 'はい。稼働率、曜日、シーズン、リードタイム、競合料金に基づく自動ルールを設定できます。ルールは手動介入なしで自動実行されます。' },
      { question: '競合料金を追跡できますか？', answer: 'はい。主要OTAでの価格を当社モニターが追跡します。競合が料金を変更すると自動アラートを受信できます。' },
    ],
    ctaTitle: 'ホテルの収益ポテンシャルを解放しましょう',
    ctaSubtitle: '機会損失をなくしましょう。データドリブンのレベニュー管理は14日間の無料トライアルから始まります。',
    relatedPages: [
      { label: 'チャネルマネージャー', description: '最適化された料金を100以上のチャネルに配信' },
      { label: 'ホテル管理ソフトウェア', description: 'レベニューツール内蔵の完全プラットフォーム' },
      { label: 'ホテルPMS', description: '分析機能統合のプロパティ管理' },
    ],
  },
};
